/**
 * FormField Component - Enhanced input with icon, validation, and error display
 *
 * This component provides a consistent form input experience with:
 * - Left icon support with dynamic coloring on focus
 * - Real-time validation with error messages
 * - Accessible error display
 * - Consistent modern styling
 *
 * @example
 * ```tsx
 * <FormField
 *   type="email"
 *   label="Email Address"
 *   placeholder="name@example.com"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   onBlur={() => validateEmail(email)}
 *   error={emailError}
 *   leftIcon={<Mail size={18} />}
 *   required
 * />
 * ```
 */

import React, { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';
import { AlertCircle } from 'lucide-react';

const formFieldVariants = cva(
  // Base input classes
  'w-full pl-11 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-200',
  {
    variants: {
      state: {
        default: 'bg-white border-2 border-neutral-200 hover:border-neutral-300 hover:shadow-md focus:border-[#6366F1] focus:shadow-lg focus:shadow-[#6366F1]/10 focus:bg-white',
        error: 'bg-white border-2 border-error-300 hover:border-error-400 focus:border-error-500 focus:shadow-lg focus:shadow-error-500/10 focus:bg-white',
        success: 'bg-white border-2 border-success-300 hover:border-success-400 focus:border-success-500 focus:shadow-lg focus:shadow-success-500/10 focus:bg-white',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

export interface FormFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
  onBlurValidation?: (value: string) => boolean;
}

const FormField = React.forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      containerClassName,
      id,
      value,
      onBlur,
      onBlurValidation,
      onChange,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;
    const state = hasError ? 'error' : 'default';

    // Icon classes
    const iconClasses = cn(
      'absolute left-3.5 top-1/2 -translate-y-1/2 transition-all duration-200',
      hasError ? 'text-error-500' : 'text-neutral-400 peer-focus:text-[#6366F1] peer-focus:scale-110'
    );

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      if (onBlurValidation && value !== undefined) {
        onBlurValidation(String(value));
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      }
      // Clear error when user starts typing
      if (hasError && error && onBlurValidation) {
        onBlurValidation(e.target.value);
      }
    };

    return (
      <div className={cn('w-full', containerClassName)}>
        {label && (
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor={inputId}
              className={cn(
                'block text-xs font-bold uppercase ml-1 tracking-wide',
                hasError ? 'text-error-600' : 'text-neutral-600'
              )}
            >
              {label}
              {props.required && <span className="text-error-500 ml-1">*</span>}
            </label>
          </div>
        )}

        <div className="relative group peer">
          {leftIcon && <div className={iconClasses}>{leftIcon}</div>}

          <input
            id={inputId}
            ref={ref}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            className={cn(
              formFieldVariants({ state }),
              'placeholder:text-neutral-400',
              className
            )}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 transition-all duration-200">
              {rightIcon}
            </div>
          )}
        </div>

        {hasError && (
          <div className="mt-2 flex items-center gap-2 text-error-600 text-sm animate-in fade-in slide-in-from-top-1 duration-200">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {helperText && !hasError && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField;
